import { useEffect, useMemo } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Container,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ChatBubbleOutline,
  PeopleAlt as PeopleAltIcon,
  Settings,
  Storage as StorageIcon,
  TableChart as TableChartIcon,
} from "@mui/icons-material";
import Layout from "components/Layout";
import { useGetUserQuery } from "features/auth/auth";
import { useAppSelector } from "features/store";

const LEGACY_STORAGE_PARAMS = [
  "prefix",
  "page",
  "search",
  "folder_type",
  "sort_field",
  "sort_order",
  "time_display",
] as const;

const COPY = {
  admin: {
    integrations: "Connection runs, schedules, and vendor activity.",
    dataStorage: "Browse folders, upload, and download files.",
    ai: "Chat with your configured AI assistant.",
    settings: "Profile, password, and preferences.",
    subtitle: "Choose where you want to go.",
    inviteUsers: "Invite users, roles, and platform access.",
  },
  client: {
    integrations: "See activity for your integrations.",
    dataStorage: "Browse folders with documents prepared for you.",
    ai: "Chat with your configured AI assistant.",
    settings: "Profile, password, and preferences.",
    subtitle: "Choose where you want to go.",
  },
} as const;

const USER_MGMT_CLIENT_VIEW_TOOLTIP =
  "Client View: User Management is available for this user. Open User Management in normal mode to edit.";

type TileIconKind =
  | "integrations"
  | "user-mgmt"
  | "storage"
  | "chat"
  | "settings";

function TileIcon({
  kind,
  compact,
}: {
  kind: TileIconKind;
  compact?: boolean;
}) {
  const fs = compact ? 26 : 40;
  const sx = { fontSize: fs, color: "primary.main" as const };
  switch (kind) {
    case "integrations":
      return <TableChartIcon sx={sx} />;
    case "user-mgmt":
      return <PeopleAltIcon sx={sx} />;
    case "storage":
      return <StorageIcon sx={sx} />;
    case "chat":
      return <ChatBubbleOutline sx={sx} />;
    case "settings":
      return <Settings sx={sx} />;
    default:
      return null;
  }
}

export default function PlatformHomePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userId } = useParams<{ userId?: string }>();
  const isClientView = location.pathname.startsWith("/client-view/");

  const webAccessDenied = useAppSelector((s) => s.auth.webAccessDenied);
  const clientViewState = useAppSelector((s) => s.clientView);
  const { data: user, isLoading } = useGetUserQuery(undefined, {
    skip: !!webAccessDenied || isClientView,
  });

  const queryString = searchParams.toString();

  useEffect(() => {
    const st = location.state as { initialPrefix?: string } | undefined;
    if (st?.initialPrefix) {
      const suffix = queryString ? `?${queryString}` : "";
      if (isClientView && userId) {
        navigate(`/client-view/${userId}/data-storage${suffix}`, {
          replace: true,
          state: st,
        });
      } else {
        navigate(`/data-storage${suffix}`, { replace: true, state: st });
      }
      return;
    }

    const hasLegacy = LEGACY_STORAGE_PARAMS.some((k) => searchParams.has(k));
    if (!hasLegacy) return;
    if (isClientView && !userId) return;
    const suffix = queryString ? `?${queryString}` : "";
    if (isClientView && userId) {
      navigate(`/client-view/${userId}/data-storage${suffix}`, {
        replace: true,
      });
    } else if (!isClientView) {
      navigate(`/data-storage${suffix}`, { replace: true });
    }
  }, [location.state, queryString, isClientView, userId, navigate]);

  const pp = user?.platform_profile;
  const loggedInIsPlatformAdmin =
    !!pp?.is_superuser || !!pp?.is_calibrate_manager;
  const viewedUserIsPlatformAdmin =
    clientViewState.isSuperuser || clientViewState.isCalibrateAdmin;
  const useClientFacingCopy = isClientView
    ? !viewedUserIsPlatformAdmin
    : !loggedInIsPlatformAdmin;
  const text = useClientFacingCopy ? COPY.client : COPY.admin;

  const welcomeName = useMemo(() => {
    if (isClientView) {
      return (
        clientViewState.displayName?.trim() ||
        (userId ? `User #${userId}` : "Client")
      );
    }
    if (!user) return "";
    const n = [user.first_name, user.last_name].filter(Boolean).join(" ");
    return n || user.username;
  }, [isClientView, clientViewState.displayName, user, userId]);

  const tiles = useMemo(() => {
    const out: {
      key: string;
      title: string;
      description: string;
      to: string;
      iconKind: TileIconKind;
      disabled?: boolean;
      disabledTooltip?: string;
    }[] = [];

    const integrations = isClientView
      ? clientViewState.isIntegrationUser
      : !!(
          pp?.is_superuser ||
          pp?.is_calibrate_manager ||
          pp?.is_integration_dashboard_user
        );
    const dataStorage = isClientView
      ? clientViewState.isDataStorageUser
      : !!(
          pp?.is_data_storage_user ||
          pp?.is_superuser ||
          pp?.is_calibrate_manager
        );
    const ai = isClientView
      ? clientViewState.isChatBotAllowed
      : pp?.is_ai_agent_user === true;
    const userMgmtActive =
      !isClientView && !!(pp?.is_superuser || pp?.is_calibrate_manager);
    const userMgmtDisabledInClientView =
      isClientView && viewedUserIsPlatformAdmin;

    const prefix = isClientView && userId ? `/client-view/${userId}` : "";

    if (integrations) {
      out.push({
        key: "integrations",
        title: "Integrations",
        description: text.integrations,
        to: prefix ? `${prefix}/integrations` : "/integrations",
        iconKind: "integrations",
      });
    }
    if (userMgmtActive) {
      out.push({
        key: "user-mgmt",
        title: "User Management",
        description: COPY.admin.inviteUsers,
        to: "/user-management",
        iconKind: "user-mgmt",
      });
    }
    if (userMgmtDisabledInClientView) {
      out.push({
        key: "user-mgmt",
        title: "User Management",
        description: COPY.admin.inviteUsers,
        to: "/user-management",
        iconKind: "user-mgmt",
        disabled: true,
        disabledTooltip: USER_MGMT_CLIENT_VIEW_TOOLTIP,
      });
    }
    if (dataStorage) {
      out.push({
        key: "storage",
        title: "Data Storage",
        description: text.dataStorage,
        to: prefix ? `${prefix}/data-storage` : "/data-storage",
        iconKind: "storage",
      });
    }
    if (ai) {
      out.push({
        key: "chat",
        title: "AI Agent",
        description: text.ai,
        to: prefix ? `${prefix}/chat` : "/chat",
        iconKind: "chat",
      });
    }
    out.push({
      key: "settings",
      title: "Account settings",
      description: text.settings,
      to: prefix ? `${prefix}/account-settings` : "/account-settings",
      iconKind: "settings",
    });
    return out;
  }, [
    isClientView,
    userId,
    pp,
    clientViewState,
    text,
    viewedUserIsPlatformAdmin,
  ]);

  if (!isClientView && isLoading) {
    return (
      <Layout title="Home" content="Home">
        <Container maxWidth="md" sx={{ py: 8, textAlign: "center" }}>
          <Typography color="text.secondary">Loading…</Typography>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title="Home" content="Home">
      <Container
        maxWidth="lg"
        sx={{
          py: { xs: 3, md: 5 },
          px: { xs: 2, sm: 3 },
          pb: { xs: 4, md: 6 },
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" fontWeight={800} gutterBottom>
            Welcome{welcomeName ? `, ${welcomeName}` : ""}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 640 }}
          >
            {isClientView ? (
              <>
                {text.subtitle}
                <Box component="span" sx={{ display: "block", mt: 1 }}>
                  You are viewing this page as this user would see it (read
                  only).
                </Box>
              </>
            ) : (
              text.subtitle
            )}
          </Typography>
        </Box>
        <Grid container spacing={2.5}>
          {tiles.map((t) => {
            const card = (
              <Card
                variant="outlined"
                sx={{
                  height: "100%",
                  borderRadius: 2,
                  borderColor: "divider",
                  transition: "box-shadow 0.2s, border-color 0.2s",
                  ...(!t.disabled && {
                    "&:hover": {
                      boxShadow: 2,
                      borderColor: "primary.light",
                    },
                  }),
                  ...(t.disabled && { opacity: 0.72 }),
                }}
              >
                <CardActionArea
                  disabled={t.disabled}
                  onClick={() => {
                    if (!t.disabled) navigate(t.to);
                  }}
                  sx={{ height: "100%", alignItems: "stretch", p: 0 }}
                >
                  <CardContent
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      height: "100%",
                    }}
                  >
                    <Box sx={{ mb: { xs: 1.25, sm: 1.5 } }}>
                      <TileIcon kind={t.iconKind} />
                    </Box>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      gutterBottom
                      sx={{ fontSize: { xs: "1.05rem", sm: undefined } }}
                    >
                      {t.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            );
            return (
              <Grid item xs={12} sm={6} md={4} key={t.key}>
                {t.disabled ? (
                  <Tooltip title={t.disabledTooltip ?? ""} placement="top">
                    <Box sx={{ display: "block", height: "100%" }}>{card}</Box>
                  </Tooltip>
                ) : (
                  card
                )}
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Layout>
  );
}
